import { createSignal, onMount, Show } from 'solid-js';
import type { Accessor, JSX } from 'solid-js';
import { css } from '@emotion/css';
import iro from '@jaames/iro';
import { useStore } from 'Shared/StoreProvider';
import { subdoc } from 'Common/ViewUtils';
import { updateFieldWithValue } from 'Shared/forms/Inputs';
import { ColourPickerPopup } from 'Shared/forms/ColourPickerPopup';


const FALLBACK_COLOUR = '#c87941';

// The native EyeDropper API isn't in TypeScript's DOM lib yet.
interface IEyeDropperResult { sRGBHex: string }
interface IEyeDropper { open(): Promise<IEyeDropperResult> }
declare global {
	interface Window {
		EyeDropper?: { new (): IEyeDropper };
	}
}

const swatchRemoveStyle = css({
	position:     'absolute',
	right:        -5,
	top:          -5,
	width:        14,
	height:       14,
	background:   '#555',
	color:        '#ddd',
	borderRadius: '50%',
	fontSize:     9,
	lineHeight:   '14px',
	textAlign:    'center',
	cursor:       'pointer',
	zIndex:       2,
	opacity:      0,
	transition:   'opacity 0.15s',
});

const swatchStyle = css({
	width:           26,
	height:          26,
	borderRadius:    5,
	border:          '2px solid #444',
	cursor:          'pointer',
	position:        'relative',
	flexShrink:      0,
	backgroundImage: 'repeating-conic-gradient(#888 0% 25%, #555 0% 50%)',
	backgroundSize:  '8px 8px',
	[`&:hover .${swatchRemoveStyle}`]: { opacity: 1 },
});

const swatchColourStyle = css({
	position:     'absolute',
	inset:        0,
	borderRadius: 3,
});

const inputRowStyle = css({
	display:     'flex',
	alignItems:  'stretch',
	gap:         6,
	marginTop:   8,
});

const hexInputStyle = css({
	background:   '#1a1a1a',
	color:        '#ccc',
	border:       '1px solid #3a3a3a',
	borderRadius: 4,
	padding:      '4px 7px',
	fontSize:     12,
	fontFamily:   'monospace',
	boxSizing:    'border-box',
	flex:         1,
	minWidth:     0,
});

const eyeDropperButtonStyle = css({
	display:         'flex',
	alignItems:      'center',
	justifyContent:  'center',
	flexShrink:      0,
	width:           30,
	padding:         0,
	background:      '#1a1a1a',
	border:          '1px solid #3a3a3a',
	borderRadius:    4,
	color:          '#ccc',
	cursor:          'pointer',
	'&:hover:not(:disabled)': { background: '#2a2a2a', color: '#fff' },
	'&:disabled':    { opacity: 0.4, cursor: 'not-allowed' },
	'& svg':         { width: 16, height: 16, fill: 'currentColor' },
});


// ── ColourPicker ──────────────────────────────────────────────────────────────
// Minimal, controlled colour picker: it shows a swatch and edits whatever colour
// the parent passes in via `value`, reporting changes through `onChange`. Wrap it
// in a facade (eg StoreColourPicker) to bind it to a particular data source.

interface IColourPicker {
	value?:    string | null;
	onChange:  (v: string) => void;
	onRemove?: () => void;
	noAlpha?:  boolean;
	// Fired when the popup closes, so a facade can flush a deferred write (see StoreColourPicker).
	onClose?:  () => void;
}

export function ColourPicker(P: IColourPicker): JSX.Element
{
	const [popup, setPopup] = createSignal(false);

	const open = () => {
		setPopup(true);
		P.onChange(P.value || FALLBACK_COLOUR);
	};

	const close = () => {
		setPopup(false);
		P.onClose?.();
	};

	const remove = (e: MouseEvent) => {
		e.stopPropagation();
		P.onRemove!();
	};

	return (<>
		<div class={swatchStyle} onClick={open}>
			<div class={swatchColourStyle} style={`background:${P.value || FALLBACK_COLOUR}`} />
			<Show when={P.onRemove}>
				<div class={swatchRemoveStyle} onClick={remove}>×</div>
			</Show>
		</div>

		<Show when={popup()}>
			<PickerPopup value={() => P.value} onChange={P.onChange} noAlpha={P.noAlpha} close={close} />
		</Show>
	</>);
}


// ── StoreColourPicker ─────────────────────────────────────────────────────────
// Facade binding the ColourPicker to a store field. Colour changes update the
// store in memory immediately, but — for efficiency — only persist to the DB once
// the popup closes.

interface IStoreColourPicker {
	field: string;
}

export function StoreColourPicker(P: IStoreColourPicker): JSX.Element
{
	const sw = useStore();

	const value = () => subdoc(sw.store, [...sw.location, P.field]) as string | null;

	const onChange = (v: string) => sw.setStore(...sw.location, P.field, v);

	const onClose = () => { updateFieldWithValue(sw, P.field, value()); };

	return <ColourPicker value={value()} onChange={onChange} onClose={onClose} />;
}


// ── PickerPopup ───────────────────────────────────────────────────────────────

interface IPickerPopup {
	value:    Accessor<string | null | undefined>;
	onChange: (v: string) => void;
	noAlpha?: boolean;
	close:    () => void;
}

function PickerPopup(P: IPickerPopup): JSX.Element
{
	let pickerNode!: HTMLDivElement;
	let colorPicker: InstanceType<typeof iro.ColorPicker> | undefined;

	const hasEyeDropper = typeof window !== 'undefined' && !!window.EyeDropper;

	onMount(() => {
		const layout = [
			{ component: iro.ui.Wheel },
			{ component: iro.ui.Slider, options: { sliderType: 'value' } },
		];
		if (!P.noAlpha)
			layout.push({ component: iro.ui.Slider, options: { sliderType: 'alpha' } });

		colorPicker = new iro.ColorPicker(pickerNode, {
			color: P.value() || FALLBACK_COLOUR,
			layout,
		});

		colorPicker.on('color:change', color => {
			P.onChange(P.noAlpha ? color.hexString : color.hex8String);
		});
	});

	const handleTextChange = (e: Event) => {
		const input = e.target as HTMLInputElement;
		let val = colourNameToHex(input.value) || input.value;
		if (P.noAlpha && /^#[0-9a-fA-F]{8}$/.test(val))
			val = val.slice(0, 7);
		P.onChange(val);
	};

	// Driving the iro picker cascades through its color:change handler, so the
	// hex/hex8 formatting (and onChange) stays in one place.
	const pickWithEyeDropper = async () => {
		try {
			const result = await new window.EyeDropper!().open();
			colorPicker?.color.set(result.sRGBHex);
		}
		catch {
			// User dismissed the eyedropper (eg pressed Escape); nothing to do.
		}
	};

	return (
		<ColourPickerPopup close={P.close} value={P.value()}>
			<div ref={pickerNode} />
			<div class={inputRowStyle}>
				<input
					type="text"
					class={hexInputStyle}
					value={P.value() ?? ''}
					maxlength={P.noAlpha ? 7 : 9}
					placeholder="#rrggbbaa"
					onChange={handleTextChange}
				/>
				<button
					type="button"
					class={eyeDropperButtonStyle}
					disabled={!hasEyeDropper}
					title={hasEyeDropper
						? 'Pick colour from screen'
						: 'Screen colour picker needs a Chromium browser on a secure (https or localhost) page'}
					onClick={pickWithEyeDropper}
				>
					<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
						<path d="M341.6 29.2L240.1 130.8l-9.4-9.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3l160 160c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3l-9.4-9.4L482.8 170.4c39-39 39-102.2 0-141.1s-102.2-39-141.1 0zM55.4 323.3c-15 15-23.4 35.4-23.4 56.6l0 42.4L5.4 462.2c-8.5 12.7-6.8 29.6 4 40.4s27.7 12.5 40.4 4L93.7 480l42.4 0c21.2 0 41.6-8.4 56.6-23.4L341.3 308 204 170.7 55.4 323.3z" />
					</svg>
				</button>
			</div>
		</ColourPickerPopup>
	);
}


// ── colourNameToHex ───────────────────────────────────────────────────────────
// iro doesn't understand CSS colour names, so map the common ones to hex.

function colourNameToHex(colour: string): string | false
{
	const colours: Record<string, string> = {
		'aliceblue':'#f0f8ff','antiquewhite':'#faebd7','aqua':'#00ffff','aquamarine':'#7fffd4','azure':'#f0ffff',
		'beige':'#f5f5dc','bisque':'#ffe4c4','black':'#000000','blanchedalmond':'#ffebcd','blue':'#0000ff','blueviolet':'#8a2be2','brown':'#a52a2a','burlywood':'#deb887',
		'cadetblue':'#5f9ea0','chartreuse':'#7fff00','chocolate':'#d2691e','coral':'#ff7f50','cornflowerblue':'#6495ed','cornsilk':'#fff8dc','crimson':'#dc143c','cyan':'#00ffff',
		'darkblue':'#00008b','darkcyan':'#008b8b','darkgoldenrod':'#b8860b','darkgray':'#a9a9a9','darkgreen':'#006400','darkkhaki':'#bdb76b','darkmagenta':'#8b008b','darkolivegreen':'#556b2f',
		'darkorange':'#ff8c00','darkorchid':'#9932cc','darkred':'#8b0000','darksalmon':'#e9967a','darkseagreen':'#8fbc8f','darkslateblue':'#483d8b','darkslategray':'#2f4f4f','darkturquoise':'#00ced1',
		'darkviolet':'#9400d3','deeppink':'#ff1493','deepskyblue':'#00bfff','dimgray':'#696969','dodgerblue':'#1e90ff',
		'firebrick':'#b22222','floralwhite':'#fffaf0','forestgreen':'#228b22','fuchsia':'#ff00ff',
		'gainsboro':'#dcdcdc','ghostwhite':'#f8f8ff','gold':'#ffd700','goldenrod':'#daa520','gray':'#808080','green':'#008000','greenyellow':'#adff2f',
		'honeydew':'#f0fff0','hotpink':'#ff69b4',
		'indianred':'#cd5c5c','indigo':'#4b0082','ivory':'#fffff0','khaki':'#f0e68c',
		'lavender':'#e6e6fa','lavenderblush':'#fff0f5','lawngreen':'#7cfc00','lemonchiffon':'#fffacd','lightblue':'#add8e6','lightcoral':'#f08080','lightcyan':'#e0ffff','lightgoldenrodyellow':'#fafad2',
		'lightgrey':'#d3d3d3','lightgreen':'#90ee90','lightpink':'#ffb6c1','lightsalmon':'#ffa07a','lightseagreen':'#20b2aa','lightskyblue':'#87cefa','lightslategray':'#778899','lightsteelblue':'#b0c4de',
		'lightyellow':'#ffffe0','lime':'#00ff00','limegreen':'#32cd32','linen':'#faf0e6',
		'magenta':'#ff00ff','maroon':'#800000','mediumaquamarine':'#66cdaa','mediumblue':'#0000cd','mediumorchid':'#ba55d3','mediumpurple':'#9370d8','mediumseagreen':'#3cb371','mediumslateblue':'#7b68ee',
		'mediumspringgreen':'#00fa9a','mediumturquoise':'#48d1cc','mediumvioletred':'#c71585','midnightblue':'#191970','mintcream':'#f5fffa','mistyrose':'#ffe4e1','moccasin':'#ffe4b5',
		'navajowhite':'#ffdead','navy':'#000080',
		'oldlace':'#fdf5e6','olive':'#808000','olivedrab':'#6b8e23','orange':'#ffa500','orangered':'#ff4500','orchid':'#da70d6',
		'palegoldenrod':'#eee8aa','palegreen':'#98fb98','paleturquoise':'#afeeee','palevioletred':'#d87093','papayawhip':'#ffefd5','peachpuff':'#ffdab9','peru':'#cd853f','pink':'#ffc0cb','plum':'#dda0dd','powderblue':'#b0e0e6','purple':'#800080',
		'rebeccapurple':'#663399','red':'#ff0000','rosybrown':'#bc8f8f','royalblue':'#4169e1',
		'saddlebrown':'#8b4513','salmon':'#fa8072','sandybrown':'#f4a460','seagreen':'#2e8b57','seashell':'#fff5ee','sienna':'#a0522d','silver':'#c0c0c0','skyblue':'#87ceeb','slateblue':'#6a5acd','slategray':'#708090','snow':'#fffafa','springgreen':'#00ff7f','steelblue':'#4682b4',
		'tan':'#d2b48c','teal':'#008080','thistle':'#d8bfd8','tomato':'#ff6347','turquoise':'#40e0d0',
		'violet':'#ee82ee',
		'wheat':'#f5deb3','white':'#ffffff','whitesmoke':'#f5f5f5',
		'yellow':'#ffff00','yellowgreen':'#9acd32',
	};
	return colours[colour.toLowerCase()] ?? false;
}
